/**
 * Bexio admin mirror API (B1b, read-only transparency).
 *
 * Targets the org-scoped `/api/admin/bexio/*` endpoints. All responses are
 * redacted server-side (no token_ref, no raw error, no raw last_error).
 *
 * Uses the exact proven admin API-client pattern (API_BASE_URL + getApiHeaders
 * + parseAdminErrorResponse) — the same path the working `/admin/belege` page
 * uses. The manual-sync call goes to the ADMIN JWT route `POST /api/admin/bexio/sync`,
 * NEVER the cron secret route. No secret / no BEXIO_PAT ever touches the browser.
 */
import {
  API_BASE_URL,
  adminErrorFrom,
  getApiHeaders,
  parseAdminErrorResponse,
} from "./core";


/** GET /api/admin/bexio/status — connection status + counts + last successful sync. */
export function fetchBexioStatus() {
  return fetch(`${API_BASE_URL}/api/admin/bexio/status`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/reconciliation — read-only payment reconciliation /
 * open-rent dashboard (B1c.3.1a). Bexio mirror is leading; totals use
 * invoice-level total_paid/total_remaining. Read-only — this helper NEVER
 * mutates and NEVER triggers a sync.
 * @returns {Promise<object>} BexioReconciliationResponse
 */
export function fetchBexioReconciliation() {
  return fetch(`${API_BASE_URL}/api/admin/bexio/reconciliation`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/expected-vs-invoiced — read-only B1c.3.2c side-by-side.
 * Expected = selected-month tenancy_revenue; Actual = CUMULATIVE Bexio actuals.
 * NOT a monthly reconciliation and NOT a delta (see SAFETY_HOOKS §9.12). This
 * helper NEVER mutates and NEVER triggers a sync. ``year``/``month`` apply only
 * to the expected side; omit them to default to the current month server-side.
 * @param {{ year?: number, month?: number }} [params]
 * @returns {Promise<object>} BexioExpectedVsInvoicedResponse
 */
export function fetchBexioExpectedVsInvoiced(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/expected-vs-invoiced${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/invoice-draft-preview — Slice 1a, READ-ONLY and
 * BEXIO-WRITE-FREE. Shows which rent-invoice drafts WOULD be needed for the
 * selected month and surfaces blockers; it NEVER creates a Bexio draft, NEVER
 * writes, and makes NO Bexio API call. ``already_exists_local`` is not derived
 * from Bexio mirror rows. ``invoice_kind`` is a non-final candidate.
 * @param {{ year?: number, month?: number, tenancy_id?: string,
 *           invoice_kind?: string }} [params]
 * @returns {Promise<object>} BexioInvoiceDraftPreviewResponse
 */
export function fetchBexioInvoiceDraftPreview(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.tenancy_id) sp.set("tenancy_id", params.tenancy_id);
  if (params.invoice_kind) sp.set("invoice_kind", params.invoice_kind);
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/invoice-draft-preview${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * Repeated query parameters, the way the sync-run endpoints expect them.
 *
 * `?entity_scope=invoices&entity_scope=contacts`, not a comma-joined string:
 * the routes declare `Optional[List[str]] = Query(...)`, so FastAPI reads the
 * repeated form and would see `"invoices,contacts"` as one unknown value — which
 * these endpoints answer with 422 rather than an empty page. Same shape the
 * invoice list already uses for its repeated `status`.
 *
 * An empty array appends nothing, which is what "no filter" means on the wire.
 */
function appendRepeated(sp, key, value) {
  if (Array.isArray(value)) {
    value.filter(Boolean).forEach((v) => sp.append(key, v));
  } else if (value) {
    sp.append(key, value);
  }
}

/**
 * GET /api/admin/bexio/sync-runs — the sync history, server-paginated.
 *
 * `total` is the FILTERED population before limit/offset, so a pager built on it
 * offers only pages that exist. Order is `started_at DESC, id DESC` and belongs
 * to the server; nothing re-sorts it here.
 *
 * `started_from` / `started_to` are INSTANTS (ISO 8601). A bare `YYYY-MM-DD`
 * parses server-side as midnight and would drop that day's runs — see
 * `buildSyncRunWindow`.
 *
 * @param {{ limit?: number, offset?: number,
 *           entity_scope?: string[]|string, status?: string[]|string,
 *           started_from?: string, started_to?: string }} [params]
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchBexioSyncRuns(params = {}) {
  const sp = new URLSearchParams();
  appendRepeated(sp, "entity_scope", params.entity_scope);
  appendRepeated(sp, "status", params.status);
  if (params.started_from) sp.set("started_from", params.started_from);
  if (params.started_to) sp.set("started_to", params.started_to);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return fetch(`${API_BASE_URL}/api/admin/bexio/sync-runs${qs ? `?${qs}` : ""}`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/sync-runs/summary — counts, success rate and average
 * duration, aggregated by PostgreSQL over the same window the list uses.
 *
 * NO `status` PARAMETER, deliberately, and not an oversight: the response IS the
 * breakdown by status. Pre-filtering the set by status would produce a success
 * rate over rows chosen for their status — 100 % whenever the table is filtered
 * to successes. The endpoint does not accept one either.
 *
 * The response echoes `period_start`, `period_end` and `entity_scopes`, so the
 * caller can show which window a figure describes instead of assuming.
 *
 * @param {{ entity_scope?: string[]|string,
 *           started_from?: string, started_to?: string }} [params]
 * @returns {Promise<object>}
 */
export function fetchBexioSyncRunsSummary(params = {}) {
  const sp = new URLSearchParams();
  appendRepeated(sp, "entity_scope", params.entity_scope);
  if (params.started_from) sp.set("started_from", params.started_from);
  if (params.started_to) sp.set("started_to", params.started_to);
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/sync-runs/summary${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/sync-runs/daily — sync-run counts per SWISS CALENDAR DAY.
 *
 * A DIFFERENT CALENDAR FROM ITS TWO SIBLINGS, and that is the point. `/sync-runs`
 * and `/sync-runs/summary` take INSTANTS: "the last 30 days" there is
 * `now - 30 x 24h`. This one answers "how did the 19th of August go", which is a
 * civil date and only exists once a zone is named. The backend names Europe/Zurich,
 * says so in `timezone`, and returns exactly `days` buckets — zero-filled, oldest
 * first.
 *
 * SO THE CALLER FORMATS AND NOTHING ELSE. No day generation, no gap filling, no
 * UTC-to-Zurich bucketing, no status tallying. Every one of those would re-derive
 * a rule this endpoint exists to own, and would derive it in the browser's zone.
 *
 * `entity_scope` is REPEATED, never comma-joined — `appendRepeated` handles that,
 * and the backend answers 422 to `invoices,contacts` rather than helpfully
 * splitting it.
 *
 * @param {{ days?: number, entity_scope?: string[] }} [params]
 * @returns {Promise<{ timezone: string, days: number, start_date: string,
 *                     end_date: string, entity_scopes: string[],
 *                     items: Array<{ date: string, total_count: number,
 *                                    running_count: number, success_count: number,
 *                                    error_count: number, partial_count: number,
 *                                    terminal_count: number }> }>}
 */
export function fetchBexioSyncRunsDaily(params = {}) {
  const sp = new URLSearchParams();
  if (params.days != null) sp.set("days", String(params.days));
  appendRepeated(sp, "entity_scope", params.entity_scope);
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/sync-runs/daily${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/invoices
 * @param {{ limit?: number, offset?: number, status?: string,
 *           from_date?: string, to_date?: string }} [params]
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchBexioInvoices(params = {}) {
  const sp = new URLSearchParams();
  // Repeatable: the "Offen" KPI needs two Bexio statuses (pending + partial) and
  // writes the same field the dropdown writes, so the two cannot compete.
  if (Array.isArray(params.status)) {
    params.status.filter(Boolean).forEach((s) => sp.append("status", s));
  } else if (params.status) {
    sp.set("status", params.status);
  }
  // "mapped" | "review" — the server-side complement pair behind the
  // "Zu prüfen" KPI, so the card and the list it opens run one query.
  if (params.mapping) sp.set("mapping", params.mapping);
  // "missing" | "present" — provider presence, filtered by the SERVER so `total`
  // and the page describe the same set. `present` means "not marked missing" and
  // never "confirmed at Bexio"; the label in the UI says so.
  if (params.provider_presence) sp.set("provider_presence", params.provider_presence);
  if (params.from_date) sp.set("from_date", params.from_date);
  if (params.to_date) sp.set("to_date", params.to_date);
  // Server-side substring match over document_nr + title (NOT the contact name,
  // which is batch-filled after the query — see the route docstring).
  if (params.q) sp.set("q", params.q);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return fetch(`${API_BASE_URL}/api/admin/bexio/invoices${qs ? `?${qs}` : ""}`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * GET /api/admin/bexio/tenancies/{id}/invoices — ONE tenancy's Bexio invoices.
 *
 * A SEPARATE ENDPOINT, not `fetchBexioInvoices` with a filter, and the client
 * mirrors that rather than hiding it. Membership here is not a column: an
 * invoice belongs to a tenancy through the mirror's manual mapping OR through
 * a draft-ledger row of that tenancy — including the ledger's
 * `replaced_bexio_invoice_id`, the document Bexio deleted, which nothing else
 * links back. The backend resolves all three into ONE deduplicated set, so an
 * invoice reachable twice still arrives once and `total` counts it once.
 *
 * Server-paginated on the same `limit`/`offset` contract as the org-wide list.
 * No status filter exists here deliberately — every invoice reaches the caller,
 * `draft` and `canceled` included.
 *
 * Each item is a `BexioInvoiceRead` plus four tenancy-specific fields:
 * `tenancy_link_source`, `provider_replaced`, `rent_period` and
 * `rent_period_conflict`. `rent_period` comes ONLY from the draft ledger and is
 * `null` for every hand-mapped historical invoice — never derived from
 * `issue_date`, `due_date` or `document_nr`.
 *
 * @param {{ tenancyId: string, limit?: number, offset?: number }} params
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchBexioTenancyInvoices({ tenancyId, limit, offset } = {}) {
  const sp = new URLSearchParams();
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/tenancies/${encodeURIComponent(
      tenancyId
    )}/invoices${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * GET /api/admin/bexio/tenants/{id}/invoices — ONE TENANT's Bexio invoices (TINV-1).
 *
 * THE PERSON, NOT THE CONTRACT. Its sibling above answers "what does Bexio hold
 * for this tenancy"; this one answers "for this person", across every tenancy
 * they have ever held — ended ones included, because a tenant who moved
 * internally still owns the invoices of the contract they left. Neither is a
 * filter over the other: the backend resolves the tenant's whole tenancy set and
 * the mirror's own `tenant_id` mapping into ONE deduplicated predicate, so an
 * invoice reachable through several of those paths still arrives once and
 * `total` counts it once.
 *
 * NOT `fetchBexioInvoices({ tenant_id })` AND NOT THE INVOICE DASHBOARD'S
 * `tenant_id`. Both of those mean the narrow thing — the `bexio_invoices.tenant_id`
 * column, which only a hand mapping ever fills — and the backend pins that as
 * their intended contract. Membership here additionally includes every invoice a
 * draft-ledger row of this person's tenancies names, so the two are different
 * questions with different totals.
 *
 * PRIMARY TENANT ONLY: the tenancy set comes from `Tenancy.tenant_id`. Co-tenants
 * recorded solely as participants are not included — the existing contract, not a
 * decision taken here.
 *
 * Each item is a `BexioInvoiceRead` plus the four tenancy fields
 * (`tenancy_link_source`, `provider_replaced`, `rent_period`,
 * `rent_period_conflict`) plus two the tenant view needs: `resolved_tenancy_id`
 * and `tenancy_attribution_conflict`. The second is a real state, not a null —
 * several of this tenant's tenancies claiming one document is something the
 * schema permits and the reader refuses to resolve.
 *
 * `rent_period` still comes ONLY from the draft ledger and is `null` for every
 * hand-mapped historical invoice — never derived from `issue_date`, `due_date` or
 * `document_nr`.
 *
 * Read-only against Vantio's own mirror. No Bexio provider call happens here.
 *
 * @param {{ tenantId: string, limit?: number, offset?: number }} params
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchBexioTenantInvoices({ tenantId, limit, offset } = {}) {
  const sp = new URLSearchParams();
  if (limit != null) sp.set("limit", String(limit));
  if (offset != null) sp.set("offset", String(offset));
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/tenants/${encodeURIComponent(
      tenantId
    )}/invoices${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/invoices/{invoice_id}/positions
 * B1c.2b read-only: mirrored Bexio line-item positions for one invoice.
 * `invoiceId` is the LOCAL BexioInvoice.id (never the external bexio_invoice_id).
 * The response never contains text_html (server-sanitized to text_plain).
 * @param {string} invoiceId local BexioInvoice.id
 * @returns {Promise<{ invoice_id: string, bexio_invoice_id: number,
 *   month_from_invoice_title: string|null, positions: Array<object> }>}
 */
export function fetchBexioInvoicePositions(invoiceId) {
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/invoices/${invoiceId}/positions`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/contacts
 * @param {{ limit?: number, offset?: number }} [params]
 * @returns {Promise<{ items: Array<object>, total: number }>}
 */
export function fetchBexioContacts(params = {}) {
  const sp = new URLSearchParams();
  // Server-side substring match over nr + display_name + mail.
  if (params.q) sp.set("q", params.q);
  // "mapped" | "unmapped" — org-wide, from the stored mapping rows.
  if (params.mapping) sp.set("mapping", params.mapping);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.offset != null) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return fetch(`${API_BASE_URL}/api/admin/bexio/contacts${qs ? `?${qs}` : ""}`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * POST /api/admin/bexio/sync — admin-only manual read-only sync trigger.
 * Uses the admin JWT route (NOT the cron secret route). No Bexio write.
 * @returns {Promise<object>} BexioManualSyncResponse (redacted)
 */
export async function triggerBexioSync() {
  const res = await fetch(`${API_BASE_URL}/api/admin/bexio/sync`, {
    method: "POST",
    headers: getApiHeaders(),
  });
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


// ─── B1c-v1: Contact → Tenancy mapping (read-only for manager, write admin-only) ──


/** GET mapping status for a single Bexio contact. */
export function getBexioContactMapping(contactId) {
  return fetch(`${API_BASE_URL}/api/admin/bexio/contacts/${contactId}/mapping`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/** GET transient tenancy suggestions for a contact (writes nothing). */
export function getBexioContactMappingSuggestions(contactId) {
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/contacts/${contactId}/mapping/suggestions`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * POST confirm (create-only) a contact→tenancy mapping. Admin only.
 * @param {number|string} contactId
 * @param {{ vantio_entity_type?: string, vantio_entity_id: string, note?: string }} payload
 */
export async function confirmBexioContactMapping(contactId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/contacts/${contactId}/mapping`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({
        vantio_entity_type: "tenancy",
        vantio_entity_id: payload.vantio_entity_id,
        note: payload.note ?? null,
      }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/**
 * PATCH change (retarget) an existing contact→tenancy mapping. Admin only.
 * @param {number|string} contactId
 * @param {{ vantio_entity_type?: string, vantio_entity_id: string, note?: string }} payload
 */
export async function changeBexioContactMapping(contactId, payload) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/contacts/${contactId}/mapping`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify({
        vantio_entity_type: "tenancy",
        vantio_entity_id: payload.vantio_entity_id,
        note: payload.note ?? null,
      }),
    }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/** DELETE a confirmed contact mapping. Admin only. */
export async function deleteBexioContactMapping(contactId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/contacts/${contactId}/mapping`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
  return res.json();
}


/**
 * GET batch mapping status for the visible contacts (no N+1).
 * @param {Array<number|string>} contactIds
 * @returns {Promise<{ items: Array<object> }>}
 */
export function getBexioContactMappingStatuses(contactIds) {
  const ids = (contactIds || []).filter((v) => v != null).join(",");
  if (!ids) return Promise.resolve({ items: [] });
  const qs = new URLSearchParams({ contact_ids: ids }).toString();
  return fetch(`${API_BASE_URL}/api/admin/bexio/mapping/contacts/status?${qs}`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


// ─── B1c.2d: Invoice → Tenancy quad-mapping ─────────────────────────
// Reads: admin + manager. Writes (POST/PATCH/DELETE): admin only.
// The mapping API accepts ONLY tenancy_id — tenant/unit/room/match_source are
// derived server-side from the tenancy (Tenancy is the anchor).

/** GET mapping status for one Bexio invoice (local BexioInvoice.id). */
export function fetchBexioInvoiceMapping(invoiceId) {
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/invoices/${invoiceId}/mapping`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * GET neutral manual candidates (NOT suggestions, NOT ranked). Optional info only
 * — never render as a recommendation / preselection.
 */
export function fetchBexioInvoiceMappingCandidates(invoiceId) {
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/invoices/${invoiceId}/mapping/candidates`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/** GET batch mapping status for the visible invoices (no N+1). */
export function fetchBexioInvoiceMappingStatuses(invoiceIds) {
  const ids = (invoiceIds || []).filter((v) => v != null).join(",");
  if (!ids) return Promise.resolve({ items: [] });
  const qs = new URLSearchParams({ invoice_ids: ids }).toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/mapping/invoices/status?${qs}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

// Shared writer: attaches res.status to the thrown Error so callers can branch on
// 409 (retarget / invalid-repair) vs 404 without re-parsing the message.
async function _writeInvoiceMapping(method, invoiceId, tenancyId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/invoices/${invoiceId}/mapping`,
    {
      method,
      headers: getApiHeaders(),
      body: JSON.stringify({ tenancy_id: tenancyId }),
    }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/** POST create-only invoice→tenancy mapping. Admin only. */
export function createBexioInvoiceMapping(invoiceId, tenancyId) {
  return _writeInvoiceMapping("POST", invoiceId, tenancyId);
}

/** PATCH deliberate retarget of an existing invoice mapping. Admin only. */
export function updateBexioInvoiceMapping(invoiceId, tenancyId) {
  return _writeInvoiceMapping("PATCH", invoiceId, tenancyId);
}

/** DELETE (remove / invalid-repair) an invoice mapping. Admin only. */
export async function deleteBexioInvoiceMapping(invoiceId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/invoices/${invoiceId}/mapping`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    const err = new Error(await parseAdminErrorResponse(res));
    err.status = res.status;
    throw err;
  }
  return res.json();
}


// ════════════════════════════════════════════════════════════════════
// C5 — Rechnungsentwürfe (preflight + single create + ledger)
// ════════════════════════════════════════════════════════════════════


/**
 * GET /api/admin/bexio/invoice-drafts/preflight — what the create WOULD produce.
 *
 * READ-ONLY on the server: no ledger claim, no commit, no Bexio call. Returns the
 * real draft (title, positions with their billed windows, footer, technical checks)
 * plus the single authoritative `ready` flag. Readable by admin AND manager.
 *
 * @returns {Promise<object>} BexioDraftPreflightResponse
 */
export function fetchBexioDraftPreflight({ tenancyId, year, month }) {
  const qs = new URLSearchParams({
    tenancy_id: tenancyId,
    year: String(year),
    month: String(month),
  }).toString();
  return fetch(`${API_BASE_URL}/api/admin/bexio/invoice-drafts/preflight?${qs}`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/invoice-draft-requests — the draft-request ledger.
 *
 * Current STATE per (tenancy, rent period) — deliberately not a history: the row is
 * overwritten in place on every attempt.
 *
 * `rentPeriod` must be a first-of-month ISO date; it is passed as an inclusive
 * from/to pair because the API has no exact-date filter. Collapsing the range is safe
 * because the column is CHECK-pinned to the first of the month.
 *
 * `total` is the server's count BEFORE `limit` is applied, and callers must compare it
 * against `items.length`: past the cap the ledger is partial, so a tenancy whose draft
 * already exists can look like a fresh candidate. That is a double-create exposure, so
 * a caller that cannot page must fail closed rather than assume completeness.
 *
 * @returns {Promise<{items: object[], total: number}>}
 */
export function fetchBexioDraftRequests({ rentPeriod, tenancyId, limit = 200 } = {}) {
  const params = new URLSearchParams();
  if (rentPeriod) {
    params.set("rent_period_from", rentPeriod);
    params.set("rent_period_to", rentPeriod);
  }
  if (tenancyId) params.set("tenancy_id", tenancyId);
  params.set("limit", String(limit));
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/invoice-draft-requests?${params.toString()}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * Error carrying the STRUCTURED draft-create response body.
 *
 * The generic `parseAdminErrorResponse` mines `detail` only; the draft-create schema
 * has no `detail` key, so on 409/502/503 it would turn the body into a raw JSON blob
 * and destroy `outcome` / `reason` / `blockers`. This class keeps them.
 */
export class BexioDraftCreateError extends Error {
  constructor(message, payload, status) {
    super(message);
    this.name = "BexioDraftCreateError";
    this.status = status;
    this.outcome = payload?.outcome ?? null;
    this.reason = payload?.reason ?? null;
    this.blockers = Array.isArray(payload?.blockers) ? payload.blockers : [];
    this.bexioInvoiceId = payload?.bexio_invoice_id ?? null;
    this.documentNr = payload?.document_nr ?? null;
    this.bexioStatusId = payload?.bexio_status_id ?? null;
    this.recoverySource = payload?.recovery_source ?? null;
  }
}


/**
 * POST /api/admin/bexio/invoice-drafts — create EXACTLY ONE real Bexio draft.
 *
 * Admin-only server-side. Sends only `{tenancy_id, year, month}`; the schema forbids
 * extras, and `api_reference` / `invoice_kind` / `allow_failed_retry` are server-owned.
 *
 * NO retry of any kind: every POST can burn a real Bexio document number, so a repeat
 * is a deliberate human decision, never an automatic one.
 *
 * Parses the JSON body on BOTH the ok and the error path, because the interesting
 * information (`outcome`, `reason`, `blockers`, `document_nr`) lives in the body for
 * 200/201 AND for 409/502/503 alike.
 *
 * @returns {Promise<object>} BexioInvoiceDraftCreateResponse
 * @throws {BexioDraftCreateError} with the structured body preserved
 */
export async function createBexioInvoiceDraft({ tenancyId, year, month }) {
  const res = await fetch(`${API_BASE_URL}/api/admin/bexio/invoice-drafts`, {
    method: "POST",
    headers: { ...getApiHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ tenancy_id: tenancyId, year, month }),
  });

  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    throw new BexioDraftCreateError(
      payload?.message || payload?.reason || `HTTP ${res.status}`,
      payload,
      res.status
    );
  }
  return payload;
}


/**
 * POST /api/admin/bexio/invoice-drafts/{id}/confirm-provider-missing
 *
 * Asks the backend to check, with EXACTLY ONE provider GET and no retry, whether the
 * Bexio document this created claim points at still exists.
 *
 * 200 => the object is gone (definitive HTTP 404 at Bexio) and the ledger row has
 * moved to `provider_missing`. The body is the updated draft-request detail.
 * 409 => either the object still exists or the check was inconclusive; NOTHING was
 * changed. The two are distinguished by the German `detail` text, not by the client.
 *
 * This call never creates anything at Bexio.
 *
 * @returns {Promise<object>} BexioDraftRequestDetail
 * @throws {BexioDraftCreateError} with the structured body preserved
 */
export async function confirmBexioDraftProviderMissing(requestId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/invoice-drafts/${encodeURIComponent(requestId)}/confirm-provider-missing`,
    { method: "POST", headers: getApiHeaders() }
  );
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  if (!res.ok) {
    throw new BexioDraftCreateError(
      payload?.detail || payload?.message || `HTTP ${res.status}`,
      payload,
      res.status
    );
  }
  return payload;
}

/**
 * POST /api/admin/bexio/invoice-drafts/{id}/replace
 *
 * Creates a REPLACEMENT Bexio draft for a claim whose previous document was already
 * proven gone. Requires the row to be in `provider_missing`, which only the
 * confirmation call above can produce.
 *
 * Like `createBexioInvoiceDraft` this can burn a real Bexio document number, so the
 * caller must have obtained an explicit human confirmation first. No retry here.
 *
 * Tenancy, period and invoice kind come from the stored row — the client cannot
 * supply them and therefore cannot move a claim to a different month.
 *
 * @returns {Promise<object>} BexioDraftRequestDetail
 * @throws {BexioDraftCreateError} with the structured body preserved
 */
export async function replaceBexioDraftProviderMissing(requestId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/invoice-drafts/${encodeURIComponent(requestId)}/replace`,
    { method: "POST", headers: getApiHeaders() }
  );
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }
  if (!res.ok) {
    throw new BexioDraftCreateError(
      payload?.detail || payload?.message || `HTTP ${res.status}`,
      payload,
      res.status
    );
  }
  return payload;
}


/**
 * GET /api/admin/bexio/config — the org's stored Bexio invoice configuration.
 *
 * READ-ONLY. There is deliberately no write counterpart yet; editing config is a
 * later slice with its own validation and audit decision.
 *
 * The response is redacted server-side: it carries no `token_ref` and no raw
 * `last_error`, only the twelve stored scalars plus the connection state.
 *
 * `configured: false` means the org has no Bexio connection row. Reading this
 * endpoint never creates one — a caller must render that state, never treat it
 * as an error and never retry into a provisioning attempt.
 *
 * `missing_blockers` is the backend's canonical completeness verdict. Render it;
 * never re-derive it from `values`, or the browser grows a second rule that
 * drifts the first time a required field is added.
 *
 * @returns {Promise<object>} BexioConfigResponse
 */
export function fetchBexioConfig() {
  return fetch(`${API_BASE_URL}/api/admin/bexio/config`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * PATCH /api/admin/bexio/config — change the org's stored Bexio configuration.
 *
 * ADMIN ONLY server-side; the read above stays admin + manager. Sends EXACTLY the
 * object it is handed and nothing else, because the endpoint is a partial update:
 * a key that is absent is left untouched, and a key sent as `null` is CLEARED.
 * Passing the whole form here would turn every save into a whole-object replace
 * and would rewrite fields nobody edited.
 *
 * Errors travel through `adminErrorFrom`, not `parseAdminErrorResponse`, so
 * `err.status` survives: the page tells 403 (no permission), 404 (no connection)
 * and 422 (rejected values) apart, and a 404 must NOT be shown as a generic
 * failure — it means this org has no Bexio connection at all.
 *
 * @param {object} patch only the changed keys, already in wire form
 * @returns {Promise<object>} the full BexioConfigResponse, recomputed server-side
 */
export async function updateBexioConfig(patch) {
  const res = await fetch(`${API_BASE_URL}/api/admin/bexio/config`, {
    method: "PATCH",
    headers: { ...getApiHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}


/**
 * GET /api/admin/bexio/config/catalogs/currencies — the selectable currencies.
 *
 * ADMIN ONLY server-side, unlike `fetchBexioConfig`: this is the one call on the
 * settings surface that reaches Bexio live, and a manager has no edit controls to
 * spend it on.
 *
 * A SELECTION AID, never a source of truth. The stored configuration stays
 * authoritative: a failure here must leave `currency_id` exactly as it is, and a
 * successful load must never write anything. Options carry `{value, label}` where
 * `value` is the integer the config stores and `label` is the ISO code — a save
 * sends `currency_id: 2`, never `"EUR"`.
 *
 * Errors travel through `adminErrorFrom` so `err.status` survives: 409 (no active
 * connection), 503 (Bexio access unusable) and 502 (provider call failed) mean
 * different things to the operator.
 *
 * @returns {Promise<{options: Array<{value: number, label: string}>}>}
 */
export async function fetchBexioCurrencyCatalog() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/catalogs/currencies`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * The org's selectable Bexio SALES taxes, for the settings tax picker (D3b).
 *
 * `{ options: [{ value: <int tax id>, label: "<provider display_name>" }] }`.
 * Only ACTIVE sales taxes come back — the backend asks Bexio for exactly those
 * and refuses a response that contains anything else, so nothing here has to
 * filter and nothing here may.
 *
 * Its own function rather than a parameterised `fetchBexioCatalog(kind)`: the
 * path is then a literal at each call site and cannot be assembled from a
 * variable that reaches the browser.
 *
 * Independent of `fetchBexioCurrencyCatalog` and of the config request. Its
 * failure degrades one dropdown and nothing else.
 */
export async function fetchBexioTaxCatalog() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/catalogs/taxes`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * The org's selectable Bexio bank accounts, for the settings picker (D3c).
 *
 * `{ options: [{ value: <int account id>, label: "<provider name>" }] }` — and
 * nothing else. The backend drops the IBAN, QR-IBAN, account number, holder name
 * and address at the reader, so this client never receives banking data and no
 * component downstream can render it by accident.
 *
 * Its own function with a literal path, like the other two catalogs: the URL is
 * never assembled from a variable that reaches the browser.
 *
 * Independent of the currency and tax catalogs and of the config request. Its
 * failure degrades one dropdown and nothing else.
 */
export async function fetchBexioBankAccountCatalog() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/catalogs/bank-accounts`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * The org's selectable Bexio payment types, for the settings picker (D3d).
 *
 * `{ options: [{ value: <int payment type id>, label: "<provider name>" }] }`.
 * The label is whatever this mandant calls the payment type in Bexio; nothing in
 * this repository maps an id to a name.
 *
 * Its own function with a literal path, like the other three catalogs, and no
 * query string — the backend deliberately sends none to the provider either.
 *
 * Independent of the other catalogs and of the config request. Its failure
 * degrades one dropdown and nothing else.
 */
export async function fetchBexioPaymentTypeCatalog() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/catalogs/payment-types`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * The org's selectable Bexio DOCUMENT languages, for the settings picker (D3e).
 *
 * `{ options: [{ value: <int language id>, label: "<provider name>" }] }`. The
 * label is whatever this mandant calls the language in Bexio; nothing in this
 * repository maps an id to a language name, and there is no fallback.
 *
 * WHAT THIS IS NOT: the browser's `Accept-Language`, Vantio's own UI language, or
 * the tenant's correspondence language. It is the language Bexio renders the
 * invoice document in.
 *
 * The provider row also carries an ISO 639-1 code and four date/number
 * formatting fields. The backend drops all five at the reader, so this client
 * never receives them — in particular the ISO code, which is why no component
 * downstream can turn the label into "Sprache (xx)".
 *
 * Its own function with a literal path, like the other four catalogs, and no
 * query string — the backend deliberately sends none to the provider either.
 *
 * Independent of the other catalogs and of the config request. Its failure
 * degrades one dropdown and nothing else.
 */
export async function fetchBexioLanguageCatalog() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/catalogs/languages`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * The org's selectable Bexio USERS, for the internal-contact picker (D3f.2).
 *
 * `{ options: [{ value: <int user id>, label: "<firstname> <lastname>" }] }`.
 *
 * THE ONLY CATALOG MADE OF PEOPLE. The provider row also carries an email
 * address and two role flags (`is_accountant`, `is_superadmin`); the backend
 * drops all three at the reader, so this client never receives them and no
 * component downstream can put a colleague's address into a dropdown — not even
 * as a disambiguator when two people share a name.
 *
 * Its own function with a literal path, like the other five catalogs, and no
 * query string — the backend deliberately sends none to the provider either.
 *
 * Independent of the other catalogs and of the config request. Its failure
 * degrades one dropdown and nothing else. In particular it must never be
 * chained to the config read: the stored contact has to stay readable while
 * Bexio is unreachable.
 */
export async function fetchBexioUserCatalog() {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/catalogs/users`,
    { headers: getApiHeaders() }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}

/**
 * PATCH /api/admin/bexio/config/target-user — set or clear the internal contact.
 *
 * A ROUTE OF ITS OWN, and the separation is the point (D3f.1). This is the one
 * configuration value whose write asks Bexio a question: the server resolves the
 * chosen id into `target_user_display_name` itself. Folding it into
 * `updateBexioConfig` would put a provider call behind every settings save, so a
 * Bexio outage would block an admin from fixing a template slug.
 *
 * SENDS EXACTLY ONE FIELD. The browser may not author the display name — that is
 * what keeps the stored name and the stored id describing the same person — and
 * the endpoint forbids extras, so sending a label would be a 422 rather than a
 * silently ignored key. The catalog option's `label` therefore stops here.
 *
 * `target_user_id: null` clears both columns and performs no provider call.
 *
 * Errors travel through `adminErrorFrom` so `err.status` survives: 409 (either
 * no active connection or the chosen user no longer resolves), 503 (Bexio access
 * unusable) and 502 (provider call failed) mean different things to the operator.
 * The 409 body carries `detail.code === "target_user_not_available"` for the
 * business case — a machine-readable discriminator, because the two 409s differ
 * otherwise only in German prose.
 *
 * @param {{target_user_id: number|null}} selection
 * @returns {Promise<object>} the full BexioConfigResponse, recomputed server-side
 */
export async function updateBexioTargetUser({ target_user_id }) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/bexio/config/target-user`,
    {
      method: "PATCH",
      headers: { ...getApiHeaders(), "Content-Type": "application/json" },
      // Built field by field, never a spread of the caller's object: a spread
      // would forward whatever a future caller happened to pass — a catalog
      // option, say, with its label attached.
      body: JSON.stringify({ target_user_id: target_user_id ?? null }),
    }
  );
  if (!res.ok) throw await adminErrorFrom(res);
  return res.json();
}


/**
 * GET /api/admin/bexio/company-invoiced — company-wide invoiced revenue for one
 * INVOICE month (A1). Read-only.
 *
 * THE MONTH IS THE INVOICE MONTH, selected on `issue_date`. It is NOT the rent
 * month: Vantio issues on the 15th of the preceding month, so a rent period of
 * 2026-10 is normally invoiced on 2026-09-15. Rent-month figures come from
 * `/api/admin/revenue-analytics` and are a different question — never label a
 * value from this helper as a rent or service month.
 *
 * NOT a receivables call. It returns no open, overdue or aging figure by design;
 * `bexio_aging_service` owns those and publishes them through revenue-analytics.
 *
 * Omit `year`/`month` to default to the current Swiss business month server-side.
 *
 * `to_year`/`to_month` (P3a) extend the selection to an inclusive month WINDOW; the
 * axis is unchanged, so a window still selects on `issue_date` and still is not a
 * rent period. Both or neither: the backend answers 422 to a half-given window
 * rather than completing it from the from-month, and this helper must not complete
 * it either. Omitted entirely, the request is the single-month one it has always
 * been.
 *
 * @param {{ year?: number, month?: number, to_year?: number, to_month?: number }} [params]
 * @returns {Promise<object>} BexioCompanyInvoicedResponse
 */
export function fetchBexioCompanyInvoiced(params = {}) {
  const sp = new URLSearchParams();
  if (params.year != null) sp.set("year", String(params.year));
  if (params.month != null) sp.set("month", String(params.month));
  if (params.to_year != null && params.to_month != null) {
    sp.set("to_year", String(params.to_year));
    sp.set("to_month", String(params.to_month));
  }
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/company-invoiced${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}


/**
 * GET /api/admin/bexio/payments/recent — the newest mirrored payment receipts
 * (A1). Read-only.
 *
 * A row-level feed for a dashboard side panel, NOT cash-flow analytics: it holds
 * no period sum and performs none of the payment-vs-`total_paid` corroboration
 * `bexio_cashflow_service` does. Never present it as "cash received this month".
 *
 * Every invoice-derived field (`document_nr`, `contact_name`, and the four
 * mapping ids) may be null: payments carry no foreign key to the invoice, so the
 * lookup can miss, and a payment on a general invoice has no tenancy at all.
 * Render the row regardless — the money is real either way.
 *
 * `limit` is bounded 1..50 server-side (default 5); out-of-range values are
 * rejected with 422 rather than clamped.
 *
 * `year`/`month` (optionally extended by `to_year`/`to_month`, P3a) narrow the feed
 * to payments RECEIVED in that inclusive window — `payment_date`, the same axis the
 * cash-in KPI uses. A window makes the feed NARROWER; it does not make it a KPI, so
 * the caveat above still holds and a total taken from these rows would bypass the
 * corroboration the cashflow service performs.
 *
 * Ordering is applied AFTER the filter server-side, so this is "the latest N INSIDE
 * the window" rather than "the latest N overall, then filtered" — the latter returns
 * nothing whenever the window is not the most recent one. Undated payments belong to
 * no period and drop out once a window is named; without one they are still returned.
 *
 * Both or neither for each pair: a half-given period is a 422, not a completed one.
 *
 * @param {{ limit?: number, year?: number, month?: number, to_year?: number, to_month?: number }} [params]
 * @returns {Promise<object>} BexioRecentPaymentsResponse
 */
export function fetchBexioRecentPayments(params = {}) {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.year != null && params.month != null) {
    sp.set("year", String(params.year));
    sp.set("month", String(params.month));
    if (params.to_year != null && params.to_month != null) {
      sp.set("to_year", String(params.to_year));
      sp.set("to_month", String(params.to_month));
    }
  }
  const qs = sp.toString();
  return fetch(
    `${API_BASE_URL}/api/admin/bexio/payments/recent${qs ? `?${qs}` : ""}`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}
